/* eslint-disable */
/* istanbul ignore file */
import React, { useState, ChangeEvent } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Stack,
  Button,
  Text,
  useToast,
  Textarea,
  Box,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { UnhireTypeEnum } from '../actionsDropdown/UnhireTypeEnum';
import colors from '../../../../../styles/colors';
import useLookupsByType from '../../../../../app/hooks/useLookupsByType';
import { HIRE_FORM_REJECTION_REASONS } from '../../../../../firebase/firestore/constants';
import { getTranslatedValue } from '../../../../../utils/localizedString';
import { Candidate, CandidateHireFormDetailResponse } from '../../../../../firebase/firestore/documents/candidate';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { HireFormReviewStatus } from '../actionsDropdown/HireFormTypeEnum';
import { StatusEnum } from '../../../../dashboard/RecruiterTodoListType';

export type HrFormRejectConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  hireDetailOfCandidate: CandidateHireFormDetailResponse | undefined;
  selectedCandidate: Candidate;
  onHireDetailModalClose: () => void;
  onTodoHireActionModalClose: () => void;
  docId?: string;
  category?: string;
  // onHrFormRejectClick: (blacklist: boolean, rejectedReason: string) => Promise<void>;
};

export const HrFormRejectConfirmationModal = ({
  isOpen,
  onClose,
  hireDetailOfCandidate,
  selectedCandidate,
  onHireDetailModalClose,
  onTodoHireActionModalClose,
  docId,
  category,
}: HrFormRejectConfirmationModalProps) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [blacklistedState, setBlacklistedState] = React.useState<UnhireTypeEnum>(UnhireTypeEnum.HIREINFUTURE);
  const [isRejectLoading, setIsRejectLoading] = React.useState<boolean>(false);
  const [showTextArea, setShowTextArea] = React.useState<boolean>(false);
  const [noteContentTextArea, setNoteContentTextArea] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<boolean>(false);

  const { t, i18n } = useTranslation('hireDetail');
  const userId = useStoreState((s) => s.app.user?.id);

  const toast = useToast();

  const { reviewHireDetailsOfCandidate } = useStoreActions((actions) => actions.candidate);
  const { updateRecruiterActionStatus } = useStoreActions((actions) => actions.toDoList);

  const onHrFromRejectModalClose = () => {
    setSelectedReason('');
    setShowTextArea(false);
    setNoteContentTextArea('');
    setErrorMessage(false);
    setBlacklistedState(UnhireTypeEnum.HIREINFUTURE);
    onClose();
    onTodoHireActionModalClose();
  };

  const onHrFormRejectClick = async (blacklist: boolean, rejectedReason: string) => {
    setIsRejectLoading(true);
    const hireDetailsId = hireDetailOfCandidate?.seekerHireDetail.id;
    const candidateId = selectedCandidate.id;

    if (hireDetailOfCandidate?.seekerHireDetail.id && hireDetailsId && rejectedReason) {
      const rejectionResponse = await reviewHireDetailsOfCandidate({
        candidateId,
        hireDetailsId,
        status: HireFormReviewStatus.REJECTED,
        blacklist,
        rejectedReason,
      });
      if (rejectionResponse?.status) {
        toast({
          title: `${rejectionResponse?.title}`,
          status: 'error',
          isClosable: true,
        });
      } else {
        toast({
          title: t('hrFormRejectConfirmationModal.successfullyRejected'),
          status: 'success',
          isClosable: true,
        });
        if (userId && docId && category) {
          await updateRecruiterActionStatus({
            userId,
            docId: docId,
            candidateId,
            status: StatusEnum.COMPLETED,
            category: category,
            subCategory: 'hireFormRejection',
          });
        }
        onHireDetailModalClose();
        onHrFromRejectModalClose();
      }
    } else {
      toast({
        title: t('common:somethingWentWrong'),
        status: 'error',
        isClosable: true,
      });
    }
    setIsRejectLoading(false);
  };

  const handleDismiss = async () => {
    if (selectedReason === 'Others' && noteContentTextArea === '') {
      setErrorMessage(true);
      return;
    }
    const isBlacklisted = blacklistedState === UnhireTypeEnum.REMOVEFOREVER;
    await onHrFormRejectClick(isBlacklisted, showTextArea ? noteContentTextArea : selectedReason);
  };

  const handleBlacklistedRadioValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBlacklistedState(e.target.value as UnhireTypeEnum);
  };

  const dismissalReaons = useLookupsByType(HIRE_FORM_REJECTION_REASONS);

  const updateNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContentTextArea(e.target.value);
    setErrorMessage(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onHrFromRejectModalClose} size="xl" isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('hrFormRejectConfirmationModal.header')}</ModalHeader>

        <ModalBody>
          <Text fontSize="1rem" lineHeight="24px" mb={3} color={colors.gray[700]}>
            <p>{t('messaging:actions.hire.actionsAfterUnhire.hireHimAgain')}</p>
          </Text>
          <RadioGroup mb="1rem">
            <Stack spacing={5} direction="row">
              <Radio
                isChecked
                colorScheme="blue"
                data-testid="HireInFuture"
                value={UnhireTypeEnum.HIREINFUTURE}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleBlacklistedRadioValueChange(e)}
              >
                {t('messaging:actions.hire.actionsAfterUnhire.hireInFuture')}
              </Radio>
              <Radio
                colorScheme="blue"
                data-testid="RemoveForever"
                value={UnhireTypeEnum.REMOVEFOREVER}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleBlacklistedRadioValueChange(e)}
              >
                {t('messaging:actions.hire.actionsAfterUnhire.removeForever')}
              </Radio>
            </Stack>
          </RadioGroup>

          <Text fontWeight="semibold" mb={2}>
            {t('hrFormRejectConfirmationModal.rejectionNote')}
          </Text>

          <RadioGroup
            value={selectedReason}
            onChange={(rsn) => {
              setSelectedReason(rsn as string);
              setErrorMessage(false);
              if (rsn === 'Others') {
                setShowTextArea(true);
              } else {
                setShowTextArea(false);
                setNoteContentTextArea('');
              }
            }}
          >
            <Stack spacing={3}>
              {dismissalReaons.map((item) => (
                <Radio key={item.id} value={item.id} data-testid="dismissalReason">
                  {getTranslatedValue(item.label, i18n.language)}
                </Radio>
              ))}

              <Radio value="Others">{t('messaging:actions.dismiss.confirmationModal.others')}</Radio>
            </Stack>
          </RadioGroup>

          {showTextArea && (
            <Textarea
              value={noteContentTextArea}
              data-testid="reasonForDismissal"
              placeholder={t('hrFormRejectConfirmationModal.reasonForRejection')}
              mt={4}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
            />
          )}

          {errorMessage && (
            <Box fontStyle="italic" mt={3} mb={3} color={colors.red[500]}>
              {t('hrFormRejectConfirmationModal.otherReasonNotSelectError')}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onHrFromRejectModalClose}>
            {t('hrFormRejectConfirmationModal.cancel')}
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDismiss}
            isDisabled={!selectedReason || isRejectLoading}
            isLoading={isRejectLoading}
          >
            {t('hrFormRejectConfirmationModal.dismiss')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
